import firebase from "../../firebase/compat";
import { v4 as uuid } from "uuid";
import { db, timestamp } from "../../firebase/config";
import { client } from "../../hooks/Client";
import notDeleted from "../../helpers/notDeleted";
import buildCopiedMatrixRow from "../../helpers/synchronisations/buildCopiedMatrixRow";
import { processTargetGroup } from "../../helpers/dataHelpers/targetgroupCategories";
import { fieldsByType, isActiveLinkOfType } from "../../helpers/synchronisations/linkedSourceIds";
import scaleStartLabel from '../../helpers/questionnaire/scaleStartLabel';

const duplicateMatrixRows = async (portfolioFieldID, fieldID, question) => {
  const snap = await db
    .collection("MatrixQuestionRows")
    .where("Field", "==", portfolioFieldID)
    .get();

  // Geen verwijderde rijen meekopiëren (ADR-008).
  // AI-CONTEXT: See docs/pitfalls.md#database-hard-to-soft-delete-leaves-old-orphans
  await Promise.all(
    snap.docs.filter((doc) => notDeleted(doc.data())).map((doc) =>
      db.collection("MatrixQuestionRows").doc().set(
        buildCopiedMatrixRow(doc.data(), { fieldId: fieldID, question, client, timestamp, id: uuid() })
      )
    )
  );
};

const duplicateMatrixColumns = async (portfolioFieldID, fieldID, question) => {
  const snap = await db
    .collection("MatrixQuestionColumns")
    .where("Field", "==", portfolioFieldID)
    .get();

  await Promise.all(
    snap.docs.filter((doc) => notDeleted(doc.data())).map((doc) =>
      db.collection("MatrixQuestionColumns").doc().set({
        Title: doc.data().Title,
        Question: question,
        Field: fieldID,
        Compagny: client,
        CompagnyID: client,
        Timestamp: timestamp,
        Position: doc.data().Position,
        ID: uuid(),
      })
    )
  );
};

const acceptSynchronisation = async (docid) => {
  await db.collection("Synchronisations").doc(docid).update({
    Status: "accepted",
  });
};

const updateSynchronisationStatus = async (docid, status) => {
  await db.collection("Synchronisations").doc(docid).update({
    Status: status,
  });
};

const declineSynchronisation = async (docid) => {
  await db.collection("Synchronisations").doc(docid).update({
    Status: "declined",
  });
};

const pairProjectOutput = async (docid, projectOutputId) => {
  await db.collection("Synchronisations").doc(docid).update({
    ProjectOutput: projectOutputId,
    Status: "paired",
  });
};

const createOutputFromSynchronisation = async (
  sync,
  activityId,
  outputsLength
) => {
  const output = await db
    .collection("Outputs")
    .where("ID", "==", sync.Output)
    .get();

  for (const doc of output.docs) {
    const newOutputId = uuid();

    await db.collection("Outputs").add({
      ID: newOutputId,
      Compagny: client,
      CompagnyID: client,
      Timestamp: timestamp,
      Title: doc.data().Title,
      Position: outputsLength + 1,
      ActivityID: activityId,
      type: "manual",
    });

    await db.collection("Synchronisations").doc(sync.docid).update({
      ProjectOutput: newOutputId,
      Status: "paired",
    });
  }
};

const createEffectFromSynchronisation = async (sync) => {
  const [effectsSnap, effectSnap] = await Promise.all([
    db.collection("OutputEffects").where("CompagnyID", "==", client).get(),
    db.collection("OutputEffects").where("ID", "==", sync.Effect).limit(1).get(),
  ]);

  // Don't copy a soft-deleted source effect (ADR-008).
  const effectData = effectSnap.docs.map((d) => d.data()).filter(notDeleted)[0];
  if (!effectData) return;

  const newEffectId = uuid();

  await db.collection("OutputEffects").add({
    Effect: effectData.Effect,
    Position: effectsSnap.docs.length + 1,
    ID: newEffectId,
    Compagny: client,
    Term: "long",
    CompagnyID: client,
    Timestamp: timestamp,
    type: "manual",
    MSIId: effectData.MSIId || "",
    Standard: effectData.Standard === "true" ? "true" : "false",
  });

  const indicatorsSnap = await db
    .collection("QuestionnaireFields")
    .where("EffectId", "array-contains", sync.Effect)
    .get();

  const matrixIndicators = indicatorsSnap.docs
    .map((doc) => doc.data())
    .filter(
      (indicator) =>
        indicator.Type === "matrix-one" || indicator.Type === "matrix-multiple"
    )
    .sort((firstIndicator, secondIndicator) => {
      return (firstIndicator.Position ?? 0) - (secondIndicator.Position ?? 0);
    });

  let createdFieldId = null;

  if (matrixIndicators.length) {
    const indicator = matrixIndicators[0];
    createdFieldId = uuid();

    await db.collection("QuestionnaireFields").doc(createdFieldId).set({
      Compagny: client,
      CompagnyID: client,
      ID: createdFieldId,
      Timestamp: timestamp,
      QuestionnaireID: "",
      Type: indicator.Type,
      Question: indicator.Question || "",
      Explainer: indicator.Explainer || "",
      ReachStart: indicator.ReachStart ?? 1,
      // Beide spellingen (#313): de vragenbouwer en het invulscherm lezen ReachStartLable.
      ReachStartLable: scaleStartLabel(indicator),
      ReachStartLabel: scaleStartLabel(indicator),
      ReachEnd: indicator.ReachEnd ?? 5,
      ReachEndLabel: indicator.ReachEndLabel || "",
      Multiple: [],
      SectionID: "",
      EffectId: firebase.firestore.FieldValue.arrayUnion(newEffectId),
      Indicator: "true",
      LiveReportHidden: false,
      Standard: indicator.Standard === "true" ? "true" : "false",
      MatrixBipolar: indicator.MatrixBipolar === true,
      MSIId: indicator.MSIId || "",
      SyncId: sync.ID,
      Position: indicator.Position || 1,
      IndicatorPosition: 1,
    });

    await Promise.all([
      duplicateMatrixRows(indicator.ID, createdFieldId, indicator.Question),
      duplicateMatrixColumns(indicator.ID, createdFieldId, indicator.Question),
    ]);
  }

  const updatePayload = {
    ProjectEffect: newEffectId,
    Status: "paired",
  };

  if (createdFieldId) {
    updatePayload.ProjectQuestion = createdFieldId;
    // `Question` (the portfolio indicator field ID) is the linkField
    // fetchHierarchyData traverses to aggregate this effect into the portfolio.
    updatePayload.Question = matrixIndicators[0].ID;
  }

  await db.collection("Synchronisations").doc(sync.docid).update(updatePayload);
};

const pairExistingMSIEffect = async (sync, projectEffectId) => {
  // The project's standardised indicator → ProjectQuestion (childKey).
  // The portfolio's standardised indicator → Question (linkField). Both are
  // required for fetchHierarchyData to traverse this effect's responses into
  // the portfolio's analytics cards.
  const [indicatorsSnap, portfolioIndicatorsSnap] = await Promise.all([
    db
      .collection("QuestionnaireFields")
      .where("EffectId", "array-contains", projectEffectId)
      .where("Standard", "==", "true")
      .get(),
    db
      .collection("QuestionnaireFields")
      .where("EffectId", "array-contains", sync.Effect)
      .where("Standard", "==", "true")
      .get(),
  ]);

  const indicators = indicatorsSnap.docs
    .map((doc) => ({
      ref: doc.ref,
      ...doc.data(),
    }))
    .filter(notDeleted);

  const primaryQuestionId = indicators[0]?.ID || null;
  const portfolioQuestionId =
    portfolioIndicatorsSnap.docs
      .filter((doc) => notDeleted(doc.data()))[0]
      ?.data().ID || null;
  const batch = db.batch();
  const syncRef = db.collection("Synchronisations").doc(sync.docid);
  const syncPayload = {
    ProjectEffect: projectEffectId,
    Status: "paired",
  };

  if (primaryQuestionId) {
    syncPayload.ProjectQuestion = primaryQuestionId;
  }

  if (portfolioQuestionId) {
    syncPayload.Question = portfolioQuestionId;
  }

  batch.update(syncRef, syncPayload);

  indicators.forEach((indicator) => {
    batch.update(indicator.ref, { SyncId: sync.ID });
  });

  await batch.commit();
};

const createQuestionFromSynchronisation = async (sync, effectId) => {
  const newFieldId = uuid();
  const existingIndicatorsSnap = await db
    .collection("QuestionnaireFields")
    .where("CompagnyID", "==", client)
    .where("EffectId", "array-contains", effectId)
    .get();
  const portfolioQuestionMeta = await db
    .collection("QuestionnaireFields")
    .where("ID", "==", sync.Question)
    .limit(1)
    .get();

  const questionDocument = portfolioQuestionMeta.docs[0]?.data();

  if (!questionDocument) return;

  const questionTitle = questionDocument.Question;
  const type = questionDocument.Type;
  const explainer = questionDocument.Explainer || "";
  const reachStart = questionDocument.ReachStart || 0;
  // Beide spellingen van de bron lezen (#313); anders werd een eigen begintekst 'Helemaal niet'.
  const reachStartLabel = scaleStartLabel(questionDocument) || "Helemaal niet";
  const reachEnd = questionDocument.ReachEnd || 5;
  const reachEndLabel = questionDocument.ReachEndLabel || "Helemaal wel";
  const indicatorPosition = existingIndicatorsSnap.docs.length + 1;

  if (type === "matrix-one" || type === "matrix-multiple") {
    const [rows, columns] = await Promise.all([
      db
        .collection("MatrixQuestionRows")
        .where("Field", "==", sync.Question)
        .get(),
      db
        .collection("MatrixQuestionColumns")
        .where("Field", "==", sync.Question)
        .get(),
    ]);

    // Geen verwijderde rijen/kolommen meekopiëren (ADR-008).
    await Promise.all(
      rows.docs.filter((doc) => notDeleted(doc.data())).map((doc) =>
        db.collection("MatrixQuestionRows").doc().set(
          buildCopiedMatrixRow(doc.data(), { fieldId: newFieldId, question: questionTitle, client, timestamp, id: uuid() })
        )
      )
    );

    await Promise.all(
      columns.docs.filter((doc) => notDeleted(doc.data())).map((doc) =>
        db.collection("MatrixQuestionColumns").doc().set({
          Title: doc.data().Title,
          Question: questionTitle,
          Field: newFieldId,
          Compagny: client,
          CompagnyID: client,
          Timestamp: timestamp,
          Position: doc.data().Position,
          ID: uuid(),
        })
      )
    );
  }

  if (type === "multiple-one" || type === "multiple-multiple") {
    const options = await db
      .collection("MultipleQuestionOptions")
      .where("Field", "==", sync.Question)
      .get();

    await Promise.all(
      options.docs.map((doc) =>
        db.collection("MultipleQuestionOptions").doc().set({
          Option: doc.data().Option,
          Question: questionTitle,
          Field: newFieldId,
          Compagny: client,
          CompagnyID: client,
          Timestamp: timestamp,
          Position: doc.data().Position,
          ID: uuid(),
        })
      )
    );
  }

  await db.collection("QuestionnaireFields").doc(newFieldId).set({
    Compagny: client,
    CompagnyID: client,
    EffectId: [effectId],
    Explainer: explainer,
    ID: newFieldId,
    Indicator: "true",
    LiveReportHidden: false,
    Multiple: [],
    Position: 1,
    Question: questionTitle,
    QuestionnaireID: "",
    ReachEnd: reachEnd,
    ReachEndLable: reachEndLabel,
    ReachEndLabel: reachEndLabel,
    ReachStart: reachStart,
    ReachStartLable: reachStartLabel,
    ReachStartLabel: reachStartLabel,
    SectionID: "",
    Standard: "false",
    MatrixBipolar: questionDocument.MatrixBipolar === true,
    Timestamp: timestamp,
    Type: type,
    Position: indicatorPosition,
    IndicatorPosition: indicatorPosition,
    SyncId: sync.ID || "",
  });

  await db.collection("Synchronisations").doc(sync.docid).update({
    ProjectQuestion: newFieldId,
    Status: "paired",
  });
};

const resetPairedSynchronisation = async (docid) => {
  await db.collection("Synchronisations").doc(docid).update({
    Status: "accepted",
    ProjectOutput: null,
    ProjectEffect: null,
  });
};

const deleteSynchronisation = async (docid) => {
  await db.collection("Synchronisations").doc(docid).update({
    Status: "deleted",
  });
};

const pairProjectTargetgroup = async (sync, projectTargetgroupId) => {
  await db.collection("Synchronisations").doc(sync.docid).update({
    ProjectTargetgroup: projectTargetgroupId,
    Status: "paired",
  });
};

const createTargetgroupFromSynchronisation = async (sync, targetgroupsLength) => {
  const portfolioTargetgroupMeta = await db
    .collection("Stakeholders")
    .where("ID", "==", sync.Targetgroup)
    .limit(1)
    .get();

  const targetgroupDocument = portfolioTargetgroupMeta.docs[0]?.data();
  const name = targetgroupDocument
    ? targetgroupDocument.Organisation
    : "Nieuwe doelgroep";
  const newTargetgroupId = uuid();

  await db.collection("Stakeholders").doc(newTargetgroupId).set({
    ID: newTargetgroupId,
    Compagny: client,
    CompagnyID: client,
    Timestamp: timestamp,
    Organisation: name,
    Type: "targetgroup",
    Position: targetgroupsLength + 1,
    type: "manual",
  });

  const syncsSnap = await db
    .collection("Synchronisations")
    .where("Type", "==", "Targetgroup")
    .where("Status", "==", "accepted")
    .get();

  const batch = db.batch();

  syncsSnap.docs.forEach((doc) => {
    batch.update(db.collection("Synchronisations").doc(doc.id), {
      ProjectTargetgroup: newTargetgroupId,
      Status: "paired",
    });
  });

  await batch.commit();
};

const pairProjectCustomEventField = async (sync, fieldId) => {
  await db.collection("Synchronisations").doc(sync.docid).update({
    ProjectCustomEventField: fieldId,
    Status: "paired",
  });
};

const createCustomEventFieldFromSynchronisation = async (
  sync,
  customEventFieldsLength
) => {
  const portfolioFieldMeta = await db
    .collection("CustomEventFields")
    .where("ID", "==", sync.CustomEvent)
    .limit(1)
    .get();

  const fieldDocument = portfolioFieldMeta.docs[0]?.data();
  const fieldTitle = fieldDocument ? fieldDocument.Title : "Nieuw custom veld";
  const fieldType = fieldDocument ? fieldDocument.Type : "open";
  const newFieldId = uuid();

  await db.collection("CustomEventFields").doc(newFieldId).set({
    ID: newFieldId,
    Compagny: client,
    CompagnyID: client,
    Timestamp: timestamp,
    Title: fieldTitle,
    Type: fieldType,
    Position: customEventFieldsLength + 1,
    type: "manual",
  });

  if (fieldType === "multiple-multiple" || fieldType === "multiple-one") {
    const options = await db
      .collection("MultipleQuestionOptions")
      .where("Field", "==", sync.CustomEvent)
      .get();

    await Promise.all(
      options.docs.map((doc) =>
        db.collection("MultipleQuestionOptions").doc().set({
          Option: doc.data().Option,
          Field: newFieldId,
          Compagny: client,
          CompagnyID: client,
          Timestamp: timestamp,
          Position: doc.data().Position,
          ID: uuid(),
        })
      )
    );
  }

  await db.collection("Synchronisations").doc(sync.docid).update({
    ProjectCustomEventField: newFieldId,
    Status: "paired",
  });
};

const pairProjectEventField = async (sync, fieldId) => {
  await db.collection("Synchronisations").doc(sync.docid).update({
    ProjectEvent: fieldId,
    Status: "paired",
  });
};

// Shape of an EventForm doc — shared by the accept-side (resolveOrCreateEventForm)
// and subscribe-side (subscribeToEventFields) on-demand creation paths.
const buildEventFormDoc = (activityId, eventFormId) => ({
  ID: eventFormId,
  ActivityID: activityId,
  Title: "Nieuw formulier",
  Description: "Vul hier de resultaten van de activiteit in",
  Date: timestamp,
  Compagny: client,
  CompagnyID: client,
  Version: "2",
  Position: 1,
  Timestamp: timestamp,
});

// Resolve the activity's EventForm, creating one on demand when the activity
// has none (e.g. an activity pulled via subscribeToActivity, which copies the
// activity but not its form). Returns the EventForm's business `ID`.
const resolveOrCreateEventForm = async (activityId) => {
  const snap = await db
    .collection("EventForms")
    .where("ActivityID", "==", activityId)
    .limit(1)
    .get();

  if (!snap.empty) return snap.docs[0].data().ID;

  const eventFormId = uuid();
  await db.collection("EventForms").add(buildEventFormDoc(activityId, eventFormId));

  return eventFormId;
};

const createEventFieldFromSynchronisation = async (sync, activityId) => {
  const portfolioFieldMeta = await db
    .collection("EventFields")
    .where("ID", "==", sync.Event)
    .limit(1)
    .get();

  const fieldDocument = portfolioFieldMeta.docs[0]?.data();
  const fieldTitle = fieldDocument ? fieldDocument.Title : "Nieuw veld";
  const fieldType = fieldDocument ? fieldDocument.Type : "open";

  const eventFormId = await resolveOrCreateEventForm(activityId);

  const existingFields = await db
    .collection("EventFields")
    .where("EventId", "==", eventFormId)
    .get();

  const newFieldId = uuid();

  await db.collection("EventFields").add({
    ID: newFieldId,
    EventId: eventFormId,
    Type: fieldType,
    Title: fieldTitle,
    Compagny: client,
    CompagnyID: client,
    Timestamp: timestamp,
    Position: existingFields.docs.length + 1,
  });

  if (fieldType === "multiple-one" || fieldType === "multiple-multiple") {
    const options = await db
      .collection("MultipleQuestionOptions")
      .where("Field", "==", sync.Event)
      .get();

    await Promise.all(
      options.docs.map((doc) =>
        db.collection("MultipleQuestionOptions").doc().set({
          Option: doc.data().Option,
          Field: newFieldId,
          Compagny: client,
          CompagnyID: client,
          Timestamp: timestamp,
          Position: doc.data().Position,
          ID: uuid(),
        })
      )
    );
  }

  await db.collection("Synchronisations").doc(sync.docid).update({
    ProjectEvent: newFieldId,
    Status: "paired",
  });
};

// ----------------------------------------------------------------------------
// Subscribe-side actions (#5): a project pulls items from a parent portfolio.
// Each function writes the project-side source doc(s) AND the Synchronisations
// row(s) directly with Status: 'paired' and Initiator: 'project' — there is no
// portfolio-side approval step.
// ----------------------------------------------------------------------------

// Build a Synchronisations row payload (used inside batched writes).
const buildSubscribeSync = ({ parentId, type, sourceField, sourceId, projectField, projectId, position, extra }) => ({
  ID: uuid(),
  Parent: parentId,
  Project: client,
  // Denormalised party tenants for the Synchronisations rule/query gate.
  // AI-CONTEXT: See docs/pitfalls.md#security-synchronisations-members-tenant-gate
  Members: [parentId, client].filter(Boolean),
  Type: type,
  Status: "paired",
  Initiator: "project",
  Timestamp: timestamp,
  Position: position,
  [sourceField]: sourceId,
  [projectField]: projectId,
  ...(extra || {}),
});

// Collection holding the project's own copy, per subscribe type.
const localCollectionByType = {
  MSIEffect: "OutputEffects",
  Effect: "OutputEffects",
  Activity: "Activities",
  Targetgroup: "Stakeholders",
};

// Refuse to link a portfolio item this project already has a live copy of.
// The modals lock linked items; this catches a stale list (second tab, listener
// lag). Picking an effect twice copied it and all its questions again — inbox #291.
// Same rule as linkedSourceIds: the link only counts while the own copy exists,
// because deleting an activity or targetgroup leaves its row 'paired'.
const assertNotLinked = async ({ parentId, type, sourceId, title }) => {
  const fields = fieldsByType[type];

  const syncsSnap = await db
    .collection("Synchronisations")
    .where("Parent", "==", parentId)
    .where("Project", "==", client)
    .where("Type", "==", type)
    .get();

  const localIds = syncsSnap.docs
    .map((doc) => doc.data())
    .filter((row) => isActiveLinkOfType(row, parentId, [type]) && row[fields.source] === sourceId)
    .map((row) => row[fields.project])
    .filter(Boolean);

  for (const localId of localIds) {
    const localSnap = await db
      .collection(localCollectionByType[type])
      .where("CompagnyID", "==", client)
      .where("ID", "==", localId)
      .get();

    if (localSnap.docs.some((doc) => notDeleted(doc.data()))) {
      throw new Error(`'${title}' is al gekoppeld aan deze koepel.`);
    }
  }
};

// Build a single summary 'sync-subscribe' notification (one per subscribe call).
const buildSubscribeNotification = ({ parentId, counts }) => ({
  ID: uuid(),
  Type: "sync-subscribe",
  Sender: client,
  Reciever: parentId,
  Read: false,
  Timestamp: timestamp,
  Counts: counts,
});

// Build a Question doc (shape shared between subscribeToEffect's questions
// and subscribeToQuestions). `effectId` is the project-side OutputEffects.ID
// that this question is attached to.
const buildQuestionDoc = ({ portfolioQuestion, effectId, position }) => ({
  ID: uuid(),
  Compagny: client,
  CompagnyID: client,
  Timestamp: timestamp,
  QuestionnaireID: "",
  Type: portfolioQuestion.Type || "scale",
  Question: portfolioQuestion.Question || "",
  Explainer: portfolioQuestion.Explainer || "",
  ReachStart: portfolioQuestion.ReachStart ?? 0,
  // Beide spellingen (#313): de vragenbouwer en het invulscherm lezen ReachStartLable.
  ReachStartLable: scaleStartLabel(portfolioQuestion),
  ReachStartLabel: scaleStartLabel(portfolioQuestion),
  ReachEnd: portfolioQuestion.ReachEnd ?? 5,
  ReachEndLabel: portfolioQuestion.ReachEndLabel || "",
  Multiple: [],
  SectionID: "",
  EffectId: [effectId],
  Indicator: "true",
  LiveReportHidden: false,
  Standard: portfolioQuestion.Standard === "true" ? "true" : "false",
  MSIId: portfolioQuestion.MSIId || "",
  Position: position,
  IndicatorPosition: position,
});

// Read the matrix rows/columns or multiple-choice options that belong to a
// portfolio question. They live in their own collections keyed by `Field`.
const fetchQuestionSubDocs = async (portfolioQuestionId, type) => {
  if (type === "matrix-one" || type === "matrix-multiple") {
    const [rowsSnap, columnsSnap] = await Promise.all([
      db
        .collection("MatrixQuestionRows")
        .where("Field", "==", portfolioQuestionId)
        .get(),
      db
        .collection("MatrixQuestionColumns")
        .where("Field", "==", portfolioQuestionId)
        .get(),
    ]);
    return {
      rows: rowsSnap.docs.map((doc) => doc.data()).filter(notDeleted),
      columns: columnsSnap.docs.map((doc) => doc.data()).filter(notDeleted),
      options: [],
    };
  }

  if (type === "multiple-one" || type === "multiple-multiple") {
    const optionsSnap = await db
      .collection("MultipleQuestionOptions")
      .where("Field", "==", portfolioQuestionId)
      .get();
    return {
      rows: [],
      columns: [],
      options: optionsSnap.docs.map((doc) => doc.data()),
    };
  }

  return { rows: [], columns: [], options: [] };
};

// Queue copies of a question's matrix rows/columns / multiple-choice options
// onto an open batch, re-pointing them at the newly created project question.
const addQuestionSubDocsToBatch = (batch, subDocs, fieldId, questionTitle) => {
  subDocs.rows.forEach((row) => {
    batch.set(
      db.collection("MatrixQuestionRows").doc(),
      buildCopiedMatrixRow(row, { fieldId, question: questionTitle, client, timestamp, id: uuid() })
    );
  });

  subDocs.columns.forEach((column) => {
    batch.set(db.collection("MatrixQuestionColumns").doc(), {
      Title: column.Title,
      Question: questionTitle,
      Field: fieldId,
      Compagny: client,
      CompagnyID: client,
      Timestamp: timestamp,
      Position: column.Position,
      ID: uuid(),
    });
  });

  subDocs.options.forEach((option) => {
    batch.set(db.collection("MultipleQuestionOptions").doc(), {
      Option: option.Option,
      Question: questionTitle,
      Field: fieldId,
      Compagny: client,
      CompagnyID: client,
      Timestamp: timestamp,
      Position: option.Position,
      ID: uuid(),
    });
  });
};

const pairTargetgroupToExisting = async ({
  portfolioTargetgroup,
  projectTargetgroup,
  parentId,
  syncsLength,
}) => {
  await assertNotLinked({
    parentId,
    type: "Targetgroup",
    sourceId: portfolioTargetgroup.ID,
    title: portfolioTargetgroup.Organisation,
  });

  const batch = db.batch();

  batch.set(db.collection("Synchronisations").doc(), buildSubscribeSync({
    parentId,
    type: "Targetgroup",
    sourceField: "Targetgroup",
    sourceId: portfolioTargetgroup.ID,
    projectField: "ProjectTargetgroup",
    projectId: projectTargetgroup.ID,
    position: syncsLength + 1,
  }));

  batch.set(db.collection("Notifications").doc(), buildSubscribeNotification({
    parentId,
    counts: { Targetgroup: 1 },
  }));

  await batch.commit();

  // The "added" item is the existing project targetgroup that got paired.
  return { id: projectTargetgroup.ID, docid: projectTargetgroup.docid };
};

const subscribeToTargetgroup = async ({
  portfolioTargetgroup,
  parentId,
  targetgroupsLength,
  syncsLength,
}) => {
  await assertNotLinked({
    parentId,
    type: "Targetgroup",
    sourceId: portfolioTargetgroup.ID,
    title: portfolioTargetgroup.Organisation,
  });

  // Read portfolio sub-targetgroups before opening the batch — batches can't
  // contain reads. One-time copy: future portfolio sub-targetgroup changes do
  // not propagate (no Synchronisations rows are created for sub-targetgroups).
  const portfolioSubsSnap = await db
    .collection("SubTargetgroups")
    .where("TargetgroupID", "==", portfolioTargetgroup.ID)
    .get();

  const batch = db.batch();
  const newTargetgroupId = uuid();
  const newTargetgroupRef = db.collection("Stakeholders").doc();

  // Run the copy through the same pipeline the own-add flows use, so a copied
  // doelgroep is indistinguishable from a locally created one. Copying raw
  // fields dropped `primaryTargetGroups` (the array every UI reads), leaving
  // "Primaire doelgroep" empty on the copy — bug #112. processTargetGroup fills
  // BOTH ADR-009 shapes and re-derives analysisTags/impactMapping.
  const taxonomy = processTargetGroup({
    stakeholderType: portfolioTargetgroup.stakeholderType,
    primaryTargetGroup: portfolioTargetgroup.primaryTargetGroup,
    primaryTargetGroups: portfolioTargetgroup.primaryTargetGroups,
    contextLabels: portfolioTargetgroup.contextLabels,
  });

  batch.set(newTargetgroupRef, {
    ID: newTargetgroupId,
    Compagny: client,
    CompagnyID: client,
    Timestamp: timestamp,
    Organisation: portfolioTargetgroup.Organisation || "",
    Categorie: portfolioTargetgroup.Categorie || "",
    Position: targetgroupsLength + 1,
    ...(portfolioTargetgroup.Description !== undefined && { Description: portfolioTargetgroup.Description }),
    stakeholderType: taxonomy.stakeholderType || "",
    primaryTargetGroup: taxonomy.primaryTargetGroup,
    primaryTargetGroups: taxonomy.primaryTargetGroups,
    contextLabels: taxonomy.contextLabels,
    analysisTags: taxonomy.analysisTags,
    impactMapping: taxonomy.impactMapping,
  });

  portfolioSubsSnap.docs.forEach((doc) => {
    const sub = doc.data();
    batch.set(db.collection("SubTargetgroups").doc(), {
      ID: uuid(),
      Compagny: client,
      CompagnyID: client,
      Timestamp: timestamp,
      TargetgroupID: newTargetgroupId,
      Title: sub.Title || "",
      Description: sub.Description || "",
      Position: sub.Position || 0,
      ...(sub.Color !== undefined && { Color: sub.Color }),
    });
  });

  batch.set(db.collection("Synchronisations").doc(), buildSubscribeSync({
    parentId,
    type: "Targetgroup",
    sourceField: "Targetgroup",
    sourceId: portfolioTargetgroup.ID,
    projectField: "ProjectTargetgroup",
    projectId: newTargetgroupId,
    position: syncsLength + 1,
  }));

  batch.set(db.collection("Notifications").doc(), buildSubscribeNotification({
    parentId,
    counts: { Targetgroup: 1 },
  }));

  await batch.commit();

  return { id: newTargetgroupId, docid: newTargetgroupRef.id };
};

const subscribeToActivity = async ({
  portfolioActivity,
  portfolioOutputs,
  parentId,
  activitiesLength,
  outputsLength,
  syncsLength,
}) => {
  await assertNotLinked({
    parentId,
    type: "Activity",
    sourceId: portfolioActivity.ID,
    title: portfolioActivity.Activity,
  });

  const batch = db.batch();
  const newActivityId = uuid();
  const newActivityRef = db.collection("Activities").doc();

  batch.set(newActivityRef, {
    ID: newActivityId,
    Activity: portfolioActivity.Activity || "",
    Compagny: client,
    CompagnyID: client,
    Timestamp: timestamp,
    Position: activitiesLength + 1,
    ...(portfolioActivity.Description !== undefined && { Description: portfolioActivity.Description }),
    ...(portfolioActivity.activityType && { activityType: portfolioActivity.activityType }),
    ...(portfolioActivity.activitySubtype && { activitySubtype: portfolioActivity.activitySubtype }),
    ...(portfolioActivity.deliveryMode && { deliveryMode: portfolioActivity.deliveryMode }),
    ...(portfolioActivity.estimatedEffort && { estimatedEffort: portfolioActivity.estimatedEffort }),
  });

  // Informational pairing row for the activity itself (mirrors Targetgroup/
  // Effect subscribes). Not a data-sync type in syncDefaults — it records the
  // portfolio link so the UI can mark the activity as "gekoppeld".
  batch.set(db.collection("Synchronisations").doc(), buildSubscribeSync({
    parentId,
    type: "Activity",
    sourceField: "Activity",
    sourceId: portfolioActivity.ID,
    projectField: "ProjectActivity",
    projectId: newActivityId,
    position: syncsLength + 1,
  }));

  portfolioOutputs.forEach((portfolioOutput, i) => {
    const newOutputId = uuid();

    batch.set(db.collection("Outputs").doc(), {
      ID: newOutputId,
      Compagny: client,
      CompagnyID: client,
      Timestamp: timestamp,
      Title: portfolioOutput.Title || "",
      ...(portfolioOutput.Unit && { Unit: portfolioOutput.Unit }),
      Position: outputsLength + i + 1,
      ActivityID: newActivityId,
      type: "manual",
    });

    batch.set(db.collection("Synchronisations").doc(), buildSubscribeSync({
      parentId,
      type: "Output",
      sourceField: "Output",
      sourceId: portfolioOutput.ID,
      projectField: "ProjectOutput",
      projectId: newOutputId,
      position: syncsLength + 1 + i + 1,
    }));
  });

  batch.set(db.collection("Notifications").doc(), buildSubscribeNotification({
    parentId,
    counts: { Activity: 1, Output: portfolioOutputs.length },
  }));

  await batch.commit();

  return { id: newActivityId, docid: newActivityRef.id };
};

const subscribeToOutputs = async ({
  portfolioOutputs,
  targetActivityId,
  parentId,
  outputsLength,
  syncsLength,
}) => {
  const batch = db.batch();

  portfolioOutputs.forEach((portfolioOutput, i) => {
    const newOutputId = uuid();

    batch.set(db.collection("Outputs").doc(), {
      ID: newOutputId,
      Compagny: client,
      CompagnyID: client,
      Timestamp: timestamp,
      Title: portfolioOutput.Title || "",
      ...(portfolioOutput.Unit && { Unit: portfolioOutput.Unit }),
      Position: outputsLength + i + 1,
      ActivityID: targetActivityId,
      type: "manual",
    });

    batch.set(db.collection("Synchronisations").doc(), buildSubscribeSync({
      parentId,
      type: "Output",
      sourceField: "Output",
      sourceId: portfolioOutput.ID,
      projectField: "ProjectOutput",
      projectId: newOutputId,
      position: syncsLength + i + 1,
    }));
  });

  batch.set(db.collection("Notifications").doc(), buildSubscribeNotification({
    parentId,
    counts: { Output: portfolioOutputs.length },
  }));

  await batch.commit();
};

const subscribeToEffect = async ({
  portfolioEffect,
  portfolioQuestions,
  parentId,
  effectsLength,
  syncsLength,
}) => {
  const isMSI = portfolioEffect.Standard === "true";
  await assertNotLinked({
    parentId,
    type: isMSI ? "MSIEffect" : "Effect",
    sourceId: portfolioEffect.ID,
    title: portfolioEffect.Effect,
  });

  const newEffectId = uuid();
  const effectSyncId = uuid();

  // MSI effects bundle standardised indicators with the effect. The modal only
  // has a drilldown listener for non-MSI questions, so read the MSI indicators
  // here. Non-MSI questions are passed in by the caller.
  let questionsToCopy = portfolioQuestions || [];
  if (isMSI) {
    const indicatorsSnap = await db
      .collection("QuestionnaireFields")
      .where("EffectId", "array-contains", portfolioEffect.ID)
      .get();
    questionsToCopy = indicatorsSnap.docs
      .map((doc) => doc.data())
      .filter(notDeleted)
      .filter((indicator) => indicator.CompagnyID === parentId)
      .sort((first, second) => (first.Position ?? 0) - (second.Position ?? 0));
  }

  // Matrix rows/columns and multiple-choice options live in separate
  // collections — read them before opening the batch.
  const questionSubDocs = await Promise.all(
    questionsToCopy.map((question) =>
      fetchQuestionSubDocs(question.ID, question.Type)
    )
  );

  const batch = db.batch();
  const newEffectRef = db.collection("OutputEffects").doc();

  batch.set(newEffectRef, {
    Effect: portfolioEffect.Effect || "",
    Position: effectsLength + 1,
    ID: newEffectId,
    Compagny: client,
    CompagnyID: client,
    Term: "long",
    Timestamp: timestamp,
    type: "manual",
    Standard: isMSI ? "true" : "false",
    MSIId: portfolioEffect.MSIId || "",
  });

  let firstFieldId = null;

  questionsToCopy.forEach((portfolioQuestion, i) => {
    const newFieldId = uuid();
    if (i === 0) firstFieldId = newFieldId;

    const questionDoc = buildQuestionDoc({
      portfolioQuestion,
      effectId: newEffectId,
      position: i + 1,
    });
    questionDoc.ID = newFieldId;
    // MSI indicators sync as part of the effect bundle (one MSIEffect sync
    // row), so they carry its SyncId instead of getting their own sync rows.
    if (isMSI) questionDoc.SyncId = effectSyncId;

    batch.set(db.collection("QuestionnaireFields").doc(), questionDoc);
    addQuestionSubDocsToBatch(
      batch,
      questionSubDocs[i],
      newFieldId,
      portfolioQuestion.Question || ""
    );

    if (!isMSI) {
      batch.set(db.collection("Synchronisations").doc(), buildSubscribeSync({
        parentId,
        type: "Question",
        sourceField: "Question",
        sourceId: portfolioQuestion.ID,
        projectField: "ProjectQuestion",
        projectId: newFieldId,
        position: syncsLength + 1 + i + 1,
      }));
    }
  });

  // MSIEffect sync rows need `Question` (the portfolio indicator field ID) as
  // well as `ProjectQuestion` — fetchHierarchyData uses `Question` as the
  // linkField to traverse standardised-effect responses into portfolio cards.
  // AI-CONTEXT: See docs/pitfalls.md#database-sync-write-missing-linkfield
  const effectSync = buildSubscribeSync({
    parentId,
    type: isMSI ? "MSIEffect" : "Effect",
    sourceField: "Effect",
    sourceId: portfolioEffect.ID,
    projectField: "ProjectEffect",
    projectId: newEffectId,
    position: syncsLength + 1,
    extra:
      isMSI && firstFieldId
        ? { ProjectQuestion: firstFieldId, Question: questionsToCopy[0]?.ID }
        : undefined,
  });
  effectSync.ID = effectSyncId;
  batch.set(db.collection("Synchronisations").doc(), effectSync);

  batch.set(db.collection("Notifications").doc(), buildSubscribeNotification({
    parentId,
    counts: isMSI
      ? { MSIEffect: 1 }
      : { Effect: 1, Question: questionsToCopy.length },
  }));

  await batch.commit();

  return { id: newEffectId, docid: newEffectRef.id };
};

const subscribeToQuestions = async ({
  portfolioQuestions,
  targetEffectId,
  parentId,
  fieldsLength,
  syncsLength,
}) => {
  // Matrix rows/columns and multiple-choice options live in separate
  // collections — read them before opening the batch.
  const questionSubDocs = await Promise.all(
    portfolioQuestions.map((question) =>
      fetchQuestionSubDocs(question.ID, question.Type)
    )
  );

  const batch = db.batch();

  portfolioQuestions.forEach((portfolioQuestion, i) => {
    const newFieldId = uuid();
    const questionDoc = buildQuestionDoc({
      portfolioQuestion,
      effectId: targetEffectId,
      position: fieldsLength + i + 1,
    });
    questionDoc.ID = newFieldId;

    batch.set(db.collection("QuestionnaireFields").doc(), questionDoc);
    addQuestionSubDocsToBatch(
      batch,
      questionSubDocs[i],
      newFieldId,
      portfolioQuestion.Question || ""
    );

    batch.set(db.collection("Synchronisations").doc(), buildSubscribeSync({
      parentId,
      type: "Question",
      sourceField: "Question",
      sourceId: portfolioQuestion.ID,
      projectField: "ProjectQuestion",
      projectId: newFieldId,
      position: syncsLength + i + 1,
    }));
  });

  batch.set(db.collection("Notifications").doc(), buildSubscribeNotification({
    parentId,
    counts: { Question: portfolioQuestions.length },
  }));

  await batch.commit();
};

// Subscribe to an activity's non-output event fields: create matching
// EventFields on a target project activity's EventForm (created on demand if
// the activity has none) and write `Type:'Event'` sync rows. Each row carries
// both `Event` (linkField) and `ProjectEvent` (childKey) so fetchHierarchyData
// can traverse the field's responses into the portfolio.
// AI-CONTEXT: See docs/pitfalls.md#database-sync-write-missing-linkfield
const subscribeToEventFields = async ({
  portfolioEventFields,
  targetActivityId,
  parentId,
  fieldsLength,
  syncsLength,
}) => {
  // Resolve the target activity's EventForm — read before opening the batch.
  const formSnap = await db
    .collection("EventForms")
    .where("ActivityID", "==", targetActivityId)
    .get();
  const existingForm = formSnap.docs[0]?.data();

  // Matrix/multiple-choice options live in their own collection — read before
  // the batch. Only multiple-* event fields carry options.
  const optionsByField = await Promise.all(
    portfolioEventFields.map((field) =>
      field.Type === "multiple-one" || field.Type === "multiple-multiple"
        ? db
            .collection("MultipleQuestionOptions")
            .where("Field", "==", field.ID)
            .get()
            .then((snap) => snap.docs.map((doc) => doc.data()))
        : Promise.resolve([])
    )
  );

  const batch = db.batch();

  let eventFormId = existingForm?.ID;
  if (!eventFormId) {
    eventFormId = uuid();
    batch.set(
      db.collection("EventForms").doc(),
      buildEventFormDoc(targetActivityId, eventFormId)
    );
  }

  portfolioEventFields.forEach((portfolioField, i) => {
    const newFieldId = uuid();

    batch.set(db.collection("EventFields").doc(), {
      ID: newFieldId,
      EventId: eventFormId,
      Type: portfolioField.Type || "open",
      Title: portfolioField.Title || "",
      Compagny: client,
      CompagnyID: client,
      Timestamp: timestamp,
      Position: fieldsLength + i + 1,
    });

    optionsByField[i].forEach((option) => {
      batch.set(db.collection("MultipleQuestionOptions").doc(), {
        Option: option.Option,
        Field: newFieldId,
        Compagny: client,
        CompagnyID: client,
        Timestamp: timestamp,
        Position: option.Position,
        ID: uuid(),
      });
    });

    batch.set(
      db.collection("Synchronisations").doc(),
      buildSubscribeSync({
        parentId,
        type: "Event",
        sourceField: "Event",
        sourceId: portfolioField.ID,
        projectField: "ProjectEvent",
        projectId: newFieldId,
        position: syncsLength + i + 1,
      })
    );
  });

  batch.set(
    db.collection("Notifications").doc(),
    buildSubscribeNotification({
      parentId,
      counts: { Event: portfolioEventFields.length },
    })
  );

  await batch.commit();
};

export {
  duplicateMatrixRows,
  duplicateMatrixColumns,
  acceptSynchronisation,
  updateSynchronisationStatus,
  declineSynchronisation,
  pairProjectOutput,
  createOutputFromSynchronisation,
  createEffectFromSynchronisation,
  pairExistingMSIEffect,
  createQuestionFromSynchronisation,
  resetPairedSynchronisation,
  deleteSynchronisation,
  pairProjectTargetgroup,
  createTargetgroupFromSynchronisation,
  pairProjectCustomEventField,
  createCustomEventFieldFromSynchronisation,
  pairProjectEventField,
  createEventFieldFromSynchronisation,
  subscribeToTargetgroup,
  pairTargetgroupToExisting,
  subscribeToActivity,
  subscribeToOutputs,
  subscribeToEffect,
  subscribeToQuestions,
  subscribeToEventFields,
};
